// SEO metadata + FAQ content for each Free Tool (English — used for search visibility
// and on-page FAQ + FAQPage JSON-LD). Keep answers short, plain, and accurate.
import { TOOL_SEO_LOCALES } from "./toolsSeoLocales";

export const TOOL_SEO = {
  "a1c-to-glucose": {
    desc: "Free A1C to average glucose (eAG) calculator. See what your A1C means for your day-to-day blood sugar, in plain language. Private and instant.",
    faq: [
      { q: "What is estimated average glucose (eAG)?", a: "eAG converts your A1C percentage into the average blood-sugar number (mg/dL or mmol/L) it reflects over the past 2–3 months, so it's easier to relate to home glucose readings." },
      { q: "Is a lower A1C always better?", a: "Lower usually means better long-term control, but targets are personal. Very low numbers can mean low blood sugar. Discuss your goal A1C with your doctor." },
    ],
  },
  "glucose-to-a1c": {
    desc: "Free blood sugar to A1C estimator. Turn your average glucose into an approximate A1C percentage. Educational, private, and instant.",
    faq: [
      { q: "Can I replace a lab A1C with this estimate?", a: "No. This is an educational estimate from your average glucose; a lab A1C measured from blood is the accurate value your doctor uses." },
      { q: "What average glucose should I use?", a: "Use the average of many readings across different times of day for the best estimate — a single reading isn't representative." },
    ],
  },
  "bmi": {
    desc: "Free BMI calculator with US and metric units. Find your body mass index and its standard weight category, explained simply. Private and instant.",
    faq: [
      { q: "Is BMI accurate for everyone?", a: "BMI is a quick screen, not a body-fat measurement. Very muscular people may read high, and it doesn't capture where fat is stored. Use it alongside other measures." },
      { q: "What is a healthy BMI?", a: "For most adults 18.5–24.9 is considered the normal range, but a healthy weight depends on your overall health — talk with your doctor." },
    ],
  },
  "blood-pressure": {
    desc: "Free blood pressure category checker (ACC/AHA). Enter your systolic and diastolic numbers to see what your reading means and what to do next.",
    faq: [
      { q: "What is a normal blood pressure?", a: "Less than 120/80 mmHg is considered normal for most adults. 120–129 systolic is 'elevated', and 130/80 or higher is high blood pressure." },
      { q: "One high reading — should I worry?", a: "A single reading can be affected by stress, caffeine or activity. Recheck when calm and rested; if it stays high, or you feel unwell, contact your doctor." },
    ],
  },
  "egfr": {
    desc: "Free eGFR kidney function calculator using the race-free CKD-EPI 2021 equation. Estimate kidney function from creatinine, age and sex.",
    faq: [
      { q: "What does eGFR tell me?", a: "It estimates how well your kidneys filter blood. Higher is generally better; a single value isn't a diagnosis — kidney health is judged over time." },
      { q: "Why is race not used?", a: "The 2021 CKD-EPI equation is race-free, following current guidance to make estimates fairer and more consistent." },
    ],
  },
  "creatinine-clearance": {
    desc: "Free creatinine clearance calculator (Cockcroft-Gault) from age, weight, creatinine and sex. Educational estimate of kidney function and drug dosing.",
    faq: [
      { q: "How is this different from eGFR?", a: "Cockcroft-Gault uses your actual body weight and is often used for medication dosing, while eGFR is standardized to body size for staging kidney disease." },
      { q: "Which weight should I enter?", a: "This tool uses actual body weight. Clinicians may adjust for body size when using the result to dose medicines." },
    ],
  },
  "cholesterol-risk": {
    desc: "Free cholesterol risk tool: see your total-to-HDL ratio plus common heart-risk factors, explained in plain language. Private and instant.",
    faq: [
      { q: "What is a good total-to-HDL ratio?", a: "Lower is better. Under about 3.5 is favorable; higher ratios suggest more cardiovascular risk. It's one signal, not a full risk score." },
      { q: "Does this give my ASCVD risk?", a: "No. For a formal 10-year heart-disease risk estimate, ask your clinician about the ASCVD risk calculator, which uses more of your health data." },
    ],
  },
  "ldl": {
    desc: "Free LDL cholesterol calculator using the Friedewald formula from total cholesterol, HDL and triglycerides. Educational and instant.",
    faq: [
      { q: "When is the Friedewald LDL not valid?", a: "It becomes unreliable when triglycerides are 400 mg/dL (4.5 mmol/L) or higher. In that case ask for a directly measured LDL." },
      { q: "What LDL should I aim for?", a: "Targets depend on your overall heart risk. Many people aim for under 100 mg/dL, and lower if higher-risk — discuss your goal with your doctor." },
    ],
  },
  "non-hdl": {
    desc: "Free non-HDL cholesterol calculator from total cholesterol and HDL. A useful treatment target that captures all the 'bad' cholesterol.",
    faq: [
      { q: "Why is non-HDL useful?", a: "It captures every cholesterol particle that can build up in arteries, not just LDL, so it can be a better treatment target — especially with high triglycerides." },
      { q: "How do I calculate it?", a: "Non-HDL cholesterol is simply your total cholesterol minus your HDL ('good') cholesterol." },
    ],
  },
  "target-heart-rate": {
    desc: "Free target heart rate calculator. Find your moderate and vigorous exercise heart-rate zones by age (with optional Karvonen method).",
    faq: [
      { q: "How are the zones calculated?", a: "Max heart rate is estimated as 220 minus your age. Moderate is 50–70% and vigorous 70–85%. Adding your resting rate uses the more personal Karvonen method." },
      { q: "Are these zones exact?", a: "No — they're age-based estimates. Fitness, medications and health conditions matter. Slow down or stop if you feel unwell." },
    ],
  },
  "due-date": {
    desc: "Free pregnancy due date calculator using Naegele's rule from your last period. Estimate your due date and current gestational age instantly.",
    faq: [
      { q: "How is the due date estimated?", a: "It adds 280 days (40 weeks) to the first day of your last period, assuming a 28-day cycle. An ultrasound gives a more precise date." },
      { q: "How accurate is it?", a: "It's a good estimate for regular cycles, but only about 1 in 20 babies arrive exactly on the due date. Your provider may adjust it after a scan." },
    ],
  },
  "medication-dose": {
    desc: "Free weight-based medication dose calculator (mg/kg) with an optional maximum single-dose cap. Educational math only — always confirm with a professional.",
    faq: [
      { q: "Is this safe to dose real medicine?", a: "This is general mg/kg math for education only. Always confirm the correct drug, dose, frequency and product with a pharmacist or doctor before giving any medicine." },
      { q: "What does the max dose do?", a: "If you enter a single-dose cap, the result won't exceed it — helpful for medicines that have a ceiling regardless of weight." },
    ],
  },
  "ideal-body-weight": {
    desc: "Free ideal body weight calculator (Devine formula) from height and sex. A rough clinical reference, shown in kg and lb.",
    faq: [
      { q: "What is ideal body weight used for?", a: "It's a rough reference used in some medication dosing and clinical estimates. Real healthy weight varies by build, muscle and body composition." },
      { q: "Should I aim for this exact number?", a: "Not necessarily — it's a formula-based estimate, not a personal goal. Discuss a healthy weight range with your doctor." },
    ],
  },
  "waist-height-ratio": {
    desc: "Free waist-to-height ratio (WHtR) calculator — a quick check of central body fat and heart-health risk from your waist and height.",
    faq: [
      { q: "What is a healthy waist-to-height ratio?", a: "Keeping your waist under half your height (a ratio below 0.5) is a simple, widely used heart-health goal." },
      { q: "Why measure waist, not just BMI?", a: "Waist size reflects fat stored around your organs, which carries more health risk. WHtR adds useful information BMI can miss." },
    ],
  },
  "pediatric-fever": {
    desc: "Free children's fever dosing calculator for acetaminophen and ibuprofen by weight — now with common liquid strengths so you get the exact mL amount.",
    faq: [
      { q: "How much acetaminophen or ibuprofen can I give my child?", a: "Dosing is by weight (about 15 mg/kg for acetaminophen, 10 mg/kg for ibuprofen). Pick your product's strength to see the exact mL — then confirm with your pediatrician or pharmacist." },
      { q: "How do I measure the liquid?", a: "Always use the syringe or cup that comes with the medicine, never a kitchen spoon, and don't combine products that contain the same drug." },
    ],
  },
};

export const toolSeo = (slug, lang = "en") => {
  const en = TOOL_SEO[slug] || { desc: "Free, private health calculator from AskMyDoctor AI.", faq: [] };
  if (lang && lang !== "en") {
    const loc = (TOOL_SEO_LOCALES[lang] || {})[slug];
    if (loc) {
      return {
        desc: loc.desc || en.desc,
        faq: loc.faq && loc.faq.length ? loc.faq : en.faq,
      };
    }
  }
  return en;
};
